import type { ReactNode } from "react";
import {
  Bar,
  BarChart as RechartsBarChart,
  CartesianGrid,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from "recharts";
import classNames from "classnames";

import ChartSkeleton from "@/components/Skeleton/ChartSkeleton";
import ChartLegend from "./ChartLegend";
import ChartTooltip from "./ChartTooltip";

/** Value-axis ticks match LineChart. Category ticks are semibold and never wrap. */
const AXIS_TICK = { fontSize: 12, fill: "#40484C" };
const AXIS_STROKE = "#F3F3F3";
const DEFAULT_HEIGHT = 225;
const CATEGORY_CHAR_PX = 9;
const CATEGORY_AXIS_PAD = 12;
const CATEGORY_AXIS_MIN = 96;
const CATEGORY_AXIS_MAX = 180;

type CategoryTickProps = {
  x?: number;
  y?: number;
  payload?: { value?: string | number };
  textAnchor?: string;
};

function CategoryTick({ x = 0, y = 0, payload, textAnchor = "end" }: CategoryTickProps) {
  return (
    <text
      x={x}
      y={y}
      dy={textAnchor === "middle" ? 12 : 4}
      textAnchor={textAnchor}
      fill={AXIS_TICK.fill}
      fontSize={AXIS_TICK.fontSize}
      fontWeight={600}
    >
      {payload?.value}
    </text>
  );
}

function widthForCategoryLabels(labels: string[], override?: number): number {
  if (override != null) {
    return override;
  }

  const longest = labels.reduce((max, label) => Math.max(max, label.length), 0);
  return Math.min(Math.max(CATEGORY_AXIS_MIN, longest * CATEGORY_CHAR_PX + CATEGORY_AXIS_PAD), CATEGORY_AXIS_MAX);
}

export type BarChartBar = {
  dataKey: string;
  name: string;
  color: string;
};

export type BarChartProps = {
  data: Record<string, string | number>[];
  bars: BarChartBar[];
  title: string;
  subtitle?: string;
  loading?: boolean;
  className?: string;
  height?: number;
  /**
   * When false, removes the card chrome (`border`, `rounded-lg`, and `p-6`) so the chart can sit
   * inside a parent card that owns padding and border. Default keeps the self-contained card look.
   */
  bordered?: boolean;
  /**
   * Recharts layout. `vertical` draws horizontal bars (category on Y). Default `horizontal` is
   * column bars (category on X).
   */
  layout?: "horizontal" | "vertical";
  categoryKey?: string;
  showLegend?: boolean;
  showValueLabels?: boolean;
  emptyLabel?: ReactNode;
  categoryAxisWidth?: number;
  barSize?: number;
};

const BarChartComponent = ({
  data,
  bars,
  title,
  subtitle,
  loading = false,
  className,
  height = DEFAULT_HEIGHT,
  bordered = true,
  layout = "horizontal",
  categoryKey = "name",
  showLegend = true,
  showValueLabels = false,
  emptyLabel,
  categoryAxisWidth,
  barSize
}: BarChartProps) => {
  const isVertical = layout === "vertical";
  const isEmpty = !loading && data.length === 0;
  const categoryLabels = data.map((row) => String(row[categoryKey] ?? ""));
  const resolvedCategoryAxisWidth = widthForCategoryLabels(categoryLabels, categoryAxisWidth);

  return (
    <div
      className={classNames(
        "flex flex-col items-start w-full h-full",
        bordered && "border border-sys-brand-outline rounded-lg p-6",
        className
      )}
      data-testid="bar-chart"
    >
      <div className="w-full mb-6">
        <p className="text-title-medium text-sys-brand-on-surface">{title}</p>
        {subtitle ? <p className="text-body-medium text-sys-brand-on-surface-variant">{subtitle}</p> : null}
      </div>

      <div className="w-full">
        {loading ? (
          <ChartSkeleton type="bar" />
        ) : isEmpty && emptyLabel ? (
          <div className="flex w-full items-center justify-center" style={{ height }}>
            {typeof emptyLabel === "string" ? (
              <p className="m-0 text-center text-body-small text-sys-brand-on-surface-variant">{emptyLabel}</p>
            ) : (
              emptyLabel
            )}
          </div>
        ) : (
          <>
            <ResponsiveContainer width="100%" height={height}>
              <RechartsBarChart
                data={data}
                layout={layout}
                margin={{
                  top: 5,
                  right: showValueLabels ? 28 : 16,
                  left: isVertical ? 0 : 20,
                  bottom: 5
                }}
              >
                <CartesianGrid strokeDasharray="0 0" stroke={AXIS_STROKE} />
                {isVertical ? (
                  <>
                    <XAxis type="number" allowDecimals={false} tickLine={false} stroke={AXIS_STROKE} tick={AXIS_TICK} />
                    <YAxis
                      type="category"
                      dataKey={categoryKey}
                      width={resolvedCategoryAxisWidth}
                      interval={0}
                      axisLine={false}
                      tickLine={false}
                      tick={CategoryTick}
                    />
                  </>
                ) : (
                  <>
                    <XAxis dataKey={categoryKey} tickLine={false} stroke={AXIS_STROKE} tick={CategoryTick} />
                    <YAxis allowDecimals={false} tickLine={false} stroke={AXIS_STROKE} tick={AXIS_TICK} />
                  </>
                )}
                <Tooltip
                  cursor={{ fill: "transparent" }}
                  content={({ active = false, payload }) => {
                    const items =
                      payload?.map((entry) => ({
                        name: String(entry.name ?? ""),
                        value: Number(entry.value ?? 0),
                        color: String(entry.color ?? "")
                      })) ?? [];
                    const category = payload?.[0]?.payload?.[categoryKey];

                    return (
                      <ChartTooltip
                        active={active}
                        title={title}
                        subtitle={typeof category === "string" ? category : undefined}
                        items={items}
                      />
                    );
                  }}
                />
                {bars.map((bar) => (
                  <Bar
                    key={bar.dataKey}
                    dataKey={bar.dataKey}
                    name={bar.name}
                    fill={bar.color}
                    radius={isVertical ? [0, 4, 4, 0] : [4, 4, 0, 0]}
                    barSize={barSize}
                  >
                    {showValueLabels ? (
                      <LabelList
                        dataKey={bar.dataKey}
                        position={isVertical ? "right" : "top"}
                        offset={6}
                        style={{ fill: AXIS_TICK.fill, fontSize: AXIS_TICK.fontSize, fontWeight: 600 }}
                      />
                    ) : null}
                  </Bar>
                ))}
              </RechartsBarChart>
            </ResponsiveContainer>

            {showLegend ? (
              <ChartLegend
                items={bars.map((bar) => ({
                  name: bar.name,
                  color: bar.color,
                  visible: true
                }))}
              />
            ) : null}
          </>
        )}
      </div>
    </div>
  );
};

export default BarChartComponent;
